import { useState } from 'react';
import { CheckCircle2, Palette } from 'lucide-react';
import { ModalForm, Input, Textarea } from '@/contasis';
import { useWorkspace } from '@/contexts/ContextoWorkspace';
import { useToast } from '@/contexts/ContextoToast';
import { categoriaService } from '@/services/api/categorias.service';
import { formValidation, commonRules, type Validators } from '@/utils';
import type { Categoria } from '@/modelos/Configuracion';

const COLORES = [
  { name: 'Rojo', value: '#ef4444' },
  { name: 'Azul', value: '#3b82f6' },
  { name: 'Verde', value: '#10b981' },
  { name: 'Amarillo', value: '#f59e0b' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Rosa', value: '#ec4899' },
  { name: 'Gris', value: '#6b7280' },
  { name: 'Naranja', value: '#f97316' },
];

const FORM_INICIAL = { nombre: '', descripcion: '', color: '#ef4444' };

const crearValidadores = (): Validators => ({
  nombre: formValidation.createFieldValidator([
    commonRules.required('El nombre es obligatorio'),
    commonRules.minLength(3, 'Mínimo 3 caracteres'),
    commonRules.maxLength(100, 'Máximo 100 caracteres'),
  ]),
  descripcion: formValidation.createFieldValidator([
    commonRules.maxLength(500, 'Máximo 500 caracteres'),
  ]),
  color: formValidation.createFieldValidator([]),
});

interface CategoriaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (categoria: Categoria) => void;
}

export function CategoriaModal({ isOpen, onClose, onCreated }: CategoriaModalProps) {
  const { empresaId } = useWorkspace();
  const { showToast } = useToast();
  const [datos, setDatos] = useState(FORM_INICIAL);
  const [validators, setValidators] = useState<Validators>(crearValidadores);
  const [cargando, setCargando] = useState(false);

  const actualizarCampo = (field: string, value: string) => {
    setDatos(prev => ({ ...prev, [field]: value }));
    setValidators(formValidation.updateValidators(field, value, validators));
  };

  const handleClose = () => {
    setDatos(FORM_INICIAL);
    setValidators(crearValidadores());
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = formValidation.isFormValid(validators, datos as unknown as Record<string, string>);
    if (result.validators) setValidators({ ...result.validators });
    if (!result.status) {
      showToast('Corrige los errores del formulario', 'error');
      return;
    }
    if (!empresaId) {
      showToast('No se pudo identificar la empresa', 'error');
      return;
    }
    setCargando(true);
    try {
      const response = await categoriaService.create({
        empresaId,
        nombre: datos.nombre,
        descripcion: datos.descripcion || undefined,
        color: datos.color,
      });
      showToast(`Categoría "${datos.nombre}" creada correctamente`, 'success');
      onCreated(response.data);
      handleClose();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Error al crear la categoría', 'error');
    } finally {
      setCargando(false);
    }
  };

  return (
    <ModalForm
      isOpen={isOpen}
      title="Nueva categoría"
      onClose={handleClose}
      onSubmit={handleSubmit}
      submitLabel="Crear"
      isSubmitting={cargando}
      size="small"
    >
      <div className="space-y-6">
        <Input
          label="Nombre"
          required
          type="text"
          value={datos.nombre}
          onChange={(e) => actualizarCampo('nombre', e.target.value)}
          placeholder="Nombre de la categoría"
          error={formValidation.getFieldErrors('nombre', validators)[0]}
        />
        <Textarea
          label="Descripción"
          value={datos.descripcion}
          onChange={(e) => actualizarCampo('descripcion', e.target.value)}
          rows={3}
          placeholder="Descripción opcional"
          error={formValidation.getFieldErrors('descripcion', validators)[0]}
          maxLength={500}
        />
        <div>
          <label className="block text-sm font-medium text-primary mb-2">
            <span className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Color de categoría
            </span>
          </label>
          <div className="grid grid-cols-4 gap-2">
            {COLORES.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setDatos(prev => ({ ...prev, color: c.value }))}
                className={`w-full h-10 rounded-lg border-2 transition-all hover:scale-105 ${
                  datos.color === c.value
                    ? 'border-primary ring-2 ring-offset-2 ring-gray-400 shadow-lg'
                    : 'border-default hover:border-strong'
                }`}
                style={{ backgroundColor: c.value }}
                title={c.name}
              >
                {datos.color === c.value && (
                  <CheckCircle2 className="w-5 h-5 mx-auto text-white drop-shadow-lg" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </ModalForm>
  );
}

export default CategoriaModal;


